import React from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import PageSizeSelector from '../../DataTable/PageSizeSelector';
import { useBackup } from '../../../hooks/settings/useBackup';
import { BackupHistoryDTO, BackupStatus } from '../../../types/settings/backup';

const statusClasses: Record<BackupStatus, string> = {
  PENDING: 'bg-warning/15 text-warning',
  RUNNING: 'bg-info/15 text-info',
  SUCCESS: 'bg-success/15 text-success',
  FAILED: 'bg-danger/15 text-danger',
};

const statusLabels: Record<BackupStatus, string> = {
  PENDING: 'Pendiente',
  RUNNING: 'En ejecución',
  SUCCESS: 'Exitoso',
  FAILED: 'Fallido',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  return new Date(value).toLocaleString('es-SV', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return '-';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size = size / 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const BackupHistoryTable: React.FC = () => {
  const {
    history,
    paginator,
    pageSize,
    fetchHistory,
    updatePageSize,
    isLoading,
  } = useBackup();

  const currentPage = paginator?.number ?? 0;
  const totalPages = paginator?.totalPages ?? 0;

  const renderRow = (item: BackupHistoryDTO) => (
    <tr key={item.id} className="border-b border-stroke dark:border-strokedark">
      <td className="px-4 py-4 text-sm font-medium text-black dark:text-white">
        {item.fileName}
      </td>
      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
        {formatFileSize(item.fileSizeBytes)}
      </td>
      <td className="px-4 py-4">
        <span
          className={`inline-flex rounded px-2.5 py-1 text-xs font-semibold ${
            statusClasses[item.status]
          }`}
        >
          {statusLabels[item.status]}
        </span>
      </td>
      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
        {item.automatic ? 'Automático' : 'Manual'}
      </td>
      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
        {formatDateTime(item.startedAt)}
      </td>
      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
        {formatDateTime(item.completedAt)}
      </td>
      <td className="max-w-[260px] px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
        <span className="line-clamp-2">{item.errorMessage || '-'}</span>
      </td>
    </tr>
  );

  return (
    <div className="space-y-4 border-t border-stroke pt-8 dark:border-strokedark">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-black dark:text-white">
            Historial de respaldos
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Consulta las ejecuciones manuales y automáticas registradas.
          </p>
        </div>

        <button
          type="button"
          onClick={() => fetchHistory(currentPage, pageSize)}
          disabled={isLoading}
          className="inline-flex items-center justify-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium text-black hover:border-primary hover:text-primary disabled:opacity-70 dark:border-strokedark dark:text-white"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      <div className="overflow-x-auto rounded border border-stroke dark:border-strokedark">
        <table className="w-full min-w-[980px] table-auto">
          <thead className="bg-gray-2 text-left dark:bg-meta-4">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Archivo
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Tamaño
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Estado
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Tipo
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Inicio
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Fin
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                Mensaje
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-boxdark">
            {history.length > 0 ? (
              history.map(renderRow)
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No hay respaldos registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageSizeSelector
          pageSize={pageSize}
          onPageSizeChange={updatePageSize}
          options={[5, 10, 20, 50]}
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => fetchHistory(Math.max(currentPage - 1, 0), pageSize)}
            disabled={!paginator || paginator.first}
            className="inline-flex h-10 w-10 items-center justify-center rounded border border-stroke text-black hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Página {totalPages === 0 ? 0 : currentPage + 1} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() =>
              fetchHistory(
                Math.min(currentPage + 1, Math.max(totalPages - 1, 0)),
                pageSize
              )
            }
            disabled={!paginator || paginator.last}
            className="inline-flex h-10 w-10 items-center justify-center rounded border border-stroke text-black hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-strokedark dark:text-white"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackupHistoryTable;
